import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, ArrowRight, Eye, EyeOff, Edit, Plus, Check, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { getPositionsForSport } from '@/data/sportsPositions';
import { toast } from 'sonner';
import { PlayerGameDayStatus } from './PlayerGameDayStatus';
import { GameScoreboard } from './GameScoreboard';

type GameDayStatus = 'ready_to_play' | 'not_ready' | 'no_show' | 'injured' | 'substituted' | 'disqualified' | 'under_age';

interface RosterPlayer {
  id: string;
  player_id: string;
  position?: string;
  team_side?: string;
  game_day_status?: GameDayStatus;
  user_type?: 'supabase' | 'internal';
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string;
    slug?: string;
  };
}

interface TeamData {
  team_id: number;
  team_name: string;
  team_logo?: string;
  roster: RosterPlayer[];
  score?: number;
  sport_type?: string;
}

interface VisualRosterDisplayProps {
  gameId: string;
  homeTeam?: TeamData;
  awayTeam?: TeamData;
  currentUserId: string;
  userRole: string;
  userTeamSide?: 'home' | 'away' | null;
  canEdit?: boolean; // Whether user can edit positions/status
  showBothTeams?: boolean; // For venue/admin view
}

export const VisualRosterDisplay: React.FC<VisualRosterDisplayProps> = ({
  gameId,
  homeTeam,
  awayTeam,
  currentUserId,
  userRole,
  userTeamSide,
  showBothTeams = false,
  canEdit = false
}) => {
  const navigate = useNavigate();
  const [homeRoster, setHomeRoster] = useState<RosterPlayer[]>([]);
  const [awayRoster, setAwayRoster] = useState<RosterPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlayer, setEditingPlayer] = useState<string | null>(null);

  useEffect(() => {
    loadRosterData();
  }, [gameId]);

  const loadRosterData = async () => {
    try {
      setLoading(true);

      // Load roster data for both teams
      const { data: rosterData, error } = await supabase
        .from('game_rosters')
        .select(`
          id,
          player_id,
          position,
          team_side,
          game_day_status
        `)
        .eq('game_id', gameId);

      if (error) throw error;

      if (rosterData) {
        // Get user details for all players
        const playerIds = rosterData.map(r => r.player_id);
        const { data: supabaseProfiles } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, profile_photo_url, slug')
          .in('id', playerIds);

        // Process roster data
        const processedRoster = rosterData.map(r => {
          let userDetails = {};
          let userType: 'supabase' | 'internal' = 'supabase';
          
          // Check if user is in supabase profiles
          const supabaseUser = supabaseProfiles?.find(p => p.id === r.player_id);
          if (supabaseUser) {
            userDetails = supabaseUser;
            userType = 'supabase';
          }

          return {
            ...r,
            user_type: userType,
            user_details: userDetails,
            game_day_status: r.game_day_status as GameDayStatus
          } as RosterPlayer;
        });

        const homeTeamRoster = processedRoster.filter(r => r.team_side === 'home');
        const awayTeamRoster = processedRoster.filter(r => r.team_side === 'away');

        setHomeRoster(homeTeamRoster);
        setAwayRoster(awayTeamRoster);
      }
    } catch (error) {
      console.error('Error loading roster data:', error);
      toast.error('Failed to load roster data');
    } finally {
      setLoading(false);
    }
  };

  const getPositionOptions = (teamSport?: string) => {
    if (teamSport) {
      return getPositionsForSport(teamSport);
    }
    // Fallback based on teams data if available
    if (homeTeam?.sport_type) {
      return getPositionsForSport(homeTeam.sport_type);
    }
    if (awayTeam?.sport_type) {
      return getPositionsForSport(awayTeam.sport_type);
    }
    return getPositionsForSport('Basketball'); // Default fallback
  };

  const handlePositionChange = async (playerId: string, newPosition: string) => {
    try {
      const { error } = await supabase
        .from('game_rosters')
        .update({ position: newPosition })
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (error) throw error;

      // Update local state
      setHomeRoster(prev => prev.map(p => 
        p.player_id === playerId ? { ...p, position: newPosition } : p
      ));
      setAwayRoster(prev => prev.map(p => 
        p.player_id === playerId ? { ...p, position: newPosition } : p
      ));

      setEditingPlayer(null);
      toast.success('Position updated successfully');
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Failed to update position');
    }
  };

  const handleStatusChange = async (playerId: string, status: GameDayStatus) => {
    try {
      const { error: rosterError } = await supabase
        .from('game_rosters')
        .update({ game_day_status: status })
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (rosterError) throw rosterError;

      // Update local state
      const updatePlayer = (p: RosterPlayer): RosterPlayer => 
        p.player_id === playerId ? { ...p, game_day_status: status } : p;

      setHomeRoster(prev => prev.map(updatePlayer));
      setAwayRoster(prev => prev.map(updatePlayer));

      toast.success(`Player status updated to ${status.replace('_', ' ')}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update player status');
    }
  };

  const getPlayerDisplayName = (player: RosterPlayer) => {
    const details = player.user_details;
    if (details?.username) return details.username;
    if (details?.first_name && details?.last_name) {
      return `${details.first_name} ${details.last_name}`;
    }
    if (details?.first_name) return details.first_name;
    return 'Player';
  };

  const getPlayerInitials = (player: RosterPlayer) => {
    const details = player.user_details;
    if (details?.first_name && details?.last_name) {
      return `${details.first_name[0]}${details.last_name[0]}`;
    }
    if (details?.username) return details.username.slice(0, 2).toUpperCase();
    return 'P';
  };

  const getPlayerProfileUrl = (player: RosterPlayer) => {
    const details = player.user_details;
    if (player.user_type === 'internal') {
      return `/internal-profile/${details?.username || player.player_id}`;
    }
    if (details?.slug) return `/profile/${details.slug}`;
    if (details?.username) return `/profile/${details.username}`;
    return `/profile/${player.player_id}`;
  };

  const handleNavigateToProfile = (player: RosterPlayer) => {
    const profileUrl = getPlayerProfileUrl(player);
    navigate(profileUrl);
  };

  const PlayerCard = ({ player, side, teamSport }: { player: RosterPlayer; side: 'home' | 'away'; teamSport?: string }) => {
    // Skip display if no real user details
    if (!player.user_details?.username && !player.user_details?.first_name) {
      return null;
    }

    return (
      <div className="flex flex-col items-center space-y-3 relative bg-card p-6 rounded-lg border border-border min-w-[200px] max-w-[220px]">
        {/* Arrow indicator - Clickable to go to profile */}
        <div 
          className="absolute -top-3 left-1/2 transform -translate-x-1/2 cursor-pointer"
          onClick={() => handleNavigateToProfile(player)}
          title="View player profile"
        >
          <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center hover:bg-primary/80 transition-colors">
            <ArrowRight className="h-4 w-4 text-primary-foreground" />
          </div>
        </div>

        {/* Player Avatar */}
        <div className="relative mt-2">
          <Avatar className="h-20 w-20 border-4 border-muted">
            <AvatarImage src={player.user_details?.profile_photo_url} />
            <AvatarFallback className="bg-muted text-muted-foreground text-lg font-bold">
              {getPlayerInitials(player)}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Player Info */}
        <div className="text-center space-y-2 w-full">
          <div className="text-sm font-medium text-foreground truncate">
            {getPlayerDisplayName(player)}
          </div>
          
          {/* Position - Clickable if canEdit */}
          {canEdit && editingPlayer === player.player_id ? (
            <div className="flex items-center space-x-1">
              <Select
                value={player.position || 'none'}
                onValueChange={(value) => handlePositionChange(player.player_id, value === 'none' ? '' : value)}
              >
                <SelectTrigger className="w-24 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {getPositionOptions(teamSport).map(position => (
                    <SelectItem key={position} value={position}>{position}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingPlayer(null)}
                className="h-7 w-7 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <Badge 
              variant="secondary" 
              className={`text-xs ${canEdit ? 'cursor-pointer hover:bg-secondary/80' : ''}`}
              onClick={() => canEdit && setEditingPlayer(player.player_id)}
            >
              {player.position || 'No Position'}
            </Badge>
          )}

          {/* Player Status Component */}
          {canEdit && userRole && ['coach', 'team-owner'].includes(userRole) && (
            <PlayerGameDayStatus
              playerId={player.player_id}
              currentStatus={player.game_day_status || 'not_ready'}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>
    );
  };

  const TeamSection = ({ 
    team, 
    roster, 
    side, 
    score 
  }: { 
    team?: TeamData; 
    roster: RosterPlayer[]; 
    side: 'home' | 'away';
    score?: number;
  }) => {
    const readyCount = roster.filter(player => player.game_day_status === 'ready_to_play').length;
    const totalCount = roster.length;

    return (
      <div className="flex flex-col items-center space-y-8">
        {/* Team Header */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-6 mb-4">
              {/* Team Logo */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-muted overflow-hidden bg-muted">
                  {team?.team_logo ? (
                    <img 
                      src={team.team_logo} 
                      alt={`${team.team_name} Logo`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Team logo failed to load:', team.team_logo);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-bold text-center">
                            ${team.team_name.split(' ').map(word => word.charAt(0)).join('')}
                          </div>`;
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-bold text-center">
                      {team?.team_name ? team.team_name.split(' ').map(word => word.charAt(0)).join('') : 'TEAM'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Team Name */}
              <div>
                <div className="text-foreground font-bold text-xl max-w-48 truncate">
                  {team?.team_name || 'TEAM NAME GOES HERE'}
                </div>
                <Badge 
                  variant={side === 'home' ? 'default' : 'secondary'}
                  className={`${side === 'home' ? 'bg-green-600' : 'bg-blue-600'} text-white text-sm`}
                >
                  {side === 'home' ? 'HOME TEAM' : 'AWAY TEAM'}
                </Badge>
              </div>
            </div>

          {/* Player Count */}
          <div className="flex items-center justify-center space-x-2 text-foreground">
            <Users className="h-5 w-5" />
            <span className="text-lg font-medium">Players: {readyCount}/{totalCount}</span>
          </div>
        </div>

        {/* Player Grid - Better spaced */}
        <div className="grid grid-cols-2 gap-8 justify-items-center">
          {roster.slice(0, 4).filter(player => 
            player.user_details?.username || player.user_details?.first_name
          ).map((player) => (
            <PlayerCard key={player.id} player={player} side={side} teamSport={team?.sport_type} />
          ))}
          
          {/* Add empty slots if less than 4 real players and can edit */}
          {canEdit && roster.filter(p => p.user_details?.username || p.user_details?.first_name).length < 4 && (
            Array.from({ length: 4 - roster.filter(p => p.user_details?.username || p.user_details?.first_name).length }).map((_, index) => (
              <div 
                key={`empty-${index}`}
                className="flex flex-col items-center justify-center space-y-3 bg-muted/30 p-6 rounded-lg border-2 border-dashed border-border min-w-[200px] max-w-[220px] h-[200px]"
              >
                <Plus className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground text-center">Add Player</p>
              </div>
            ))
          )}
        </div>

        {/* Additional players if more than 4 */}
        {roster.filter(p => p.user_details?.username || p.user_details?.first_name).length > 4 && (
          <div className="flex justify-center space-x-8">
            {roster.filter(p => p.user_details?.username || p.user_details?.first_name).slice(4, 6).map((player) => (
              <PlayerCard key={player.id} player={player} side={side} teamSport={team?.sport_type} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const shouldShowHomeTeam = showBothTeams || userTeamSide === 'home';
  const shouldShowAwayTeam = showBothTeams || userTeamSide === 'away';

  if (loading) {
    return (
      <Card className="bg-gray-900 border-gray-700">
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <div className="ml-4 text-muted-foreground">Loading roster...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-8">
        <div className="space-y-10">
          {/* Scoreboard */}
          {showBothTeams && homeTeam && awayTeam && (
            <GameScoreboard
              gameId={gameId}
              homeTeam={{ ...homeTeam, score: homeTeam.score || 0 }}
              awayTeam={{ ...awayTeam, score: awayTeam.score || 0 }}
              sportName={homeTeam.sport_type || awayTeam.sport_type || 'Basketball'}
              canEdit={userRole === 'scorekeeper' || userRole === 'venue' || userRole === 'admin'}
              showDetailed={false}
            />
          )}

          {/* Header */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-foreground mb-2">Game Roster</h3>
            <Badge variant="outline" className="mb-2">
              {homeTeam?.sport_type || awayTeam?.sport_type || 'Basketball'}
            </Badge>
            {showBothTeams ? (
              <p className="text-muted-foreground">Venue/Admin View - Both Teams</p>
            ) : (
              <p className="text-muted-foreground">
                {userTeamSide === 'home' ? 'Home Team View' : 'Away Team View'}
              </p>
            )}
          </div>

          {/* Main Layout */}
          <div className="flex items-start justify-center space-x-16">
            {/* Home Team */}
            {shouldShowHomeTeam && (
              <div className="flex-1 max-w-2xl">
                <TeamSection 
                  team={homeTeam}
                  roster={homeRoster}
                  side="home"
                  score={homeTeam?.score}
                />
              </div>
            )}

            {/* Score Display (only if showing both teams) */}
            {showBothTeams && (
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-gray-800 border-4 border-green-500 rounded-lg p-4 text-center min-w-20">
                    <div className="text-4xl font-bold text-white">
                      {homeTeam?.score || '0'}
                    </div>
                  </div>
                  <div className="text-white text-2xl font-bold">VS</div>
                  <div className="bg-gray-800 border-4 border-blue-500 rounded-lg p-4 text-center min-w-20">
                    <div className="text-4xl font-bold text-white">
                      {awayTeam?.score || '0'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Away Team */}
            {shouldShowAwayTeam && (
              <div className="flex-1 max-w-2xl">
                <TeamSection 
                  team={awayTeam}
                  roster={awayRoster}
                  side="away"
                  score={awayTeam?.score}
                />
              </div>
            )}
          </div>

          {/* Enhanced Legend */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h4 className="font-medium text-white mb-4 text-center">Player Status Guide</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-gray-300">Ready to Play</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="text-gray-300">Not Ready</span>
              </div>
              <div className="flex items-center space-x-3">
                <ArrowRight className="h-4 w-4 text-sports-green" />
                <span className="text-gray-300">View Profile</span>
              </div>
              <div className="flex items-center space-x-3">
                <Badge variant="secondary" className="text-xs">Position</Badge>
                <span className="text-gray-300">Click to Edit</span>
              </div>
            </div>
          </div>

          {/* Toggle View Button (for team members) */}
          {!showBothTeams && ['coach', 'team-owner'].includes(userRole) && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="lg"
                className="border-gray-600 text-gray-300"
                onClick={() => {
                  toast.info('Contact venue admin for full roster view');
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                Request Full View
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};